// CMS Audit Logs Page
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useCMS } from './CMSContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Search, ChevronLeft, ChevronRight, Shield, Calendar, 
  User, FileText, Download, RefreshCw, Eye
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CMSAuditLogs = () => {
  const { getAuthHeaders } = useCMS();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [actionFilter, setActionFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expandedLog, setExpandedLog] = useState(null);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 50);
      if (actionFilter) params.append('action', actionFilter);
      if (entityFilter) params.append('entity_type', entityFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);

      const res = await axios.get(`${API}/cms/audit-logs?${params.toString()}`, { 
        headers: getAuthHeaders() 
      });
      setLogs(res.data.logs);
      setTotalPages(res.data.pages);
      setTotal(res.data.total);
    } catch (err) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [page, actionFilter, entityFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchLogs();
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);

      const res = await axios.get(`${API}/cms/audit-logs/export?${params.toString()}`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_logs_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      toast.success('Export downloaded');
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString();
  };

  const getActionColor = (action) => {
    if (action.includes('delete') || action.includes('suspend') || action.includes('deactivate')) {
      return 'bg-red-500/20 text-red-400';
    }
    if (action.includes('create') || action.includes('activate') || action.includes('reactivate')) {
      return 'bg-green-500/20 text-green-400';
    }
    if (action.includes('update') || action.includes('extend')) {
      return 'bg-yellow-500/20 text-yellow-400';
    }
    if (action.includes('login') || action.includes('logout')) {
      return 'bg-blue-500/20 text-blue-400';
    }
    if (action.includes('export') || action.includes('refund')) {
      return 'bg-purple-500/20 text-purple-400';
    }
    return 'bg-slate-500/20 text-slate-400';
  };

  const actionOptions = [
    'login', 'logout', 'create_admin', 'update_admin', 'delete_admin',
    'update_user', 'suspend_user', 'reactivate_user', 'export_user_data',
    'activate_pass', 'deactivate_pass', 'extend_pass',
    'refund_payment', 'export_payments'
  ];

  const entityOptions = ['admin', 'user', 'pass', 'payment'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Audit Logs</h1>
          <p className="text-slate-400">{total} total log entries</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExport}>
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" onClick={fetchLogs} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
            <Select value={actionFilter || "all"} onValueChange={(v) => setActionFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-48 bg-slate-900 border-slate-600">
                <SelectValue placeholder="Action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {actionOptions.map(a => (
                  <SelectItem key={a} value={a}>{a.replace('_', ' ')}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={entityFilter || "all"} onValueChange={(v) => setEntityFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-40 bg-slate-900 border-slate-600">
                <SelectValue placeholder="Entity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entities</SelectItem>
                {entityOptions.map(e => (
                  <SelectItem key={e} value={e}>{e}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-40 bg-slate-900 border-slate-600"
            />
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-40 bg-slate-900 border-slate-600"
            />
            <Button type="submit">Filter</Button>
          </form>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-slate-700 bg-slate-900/50">
                <tr>
                  <th className="text-left p-4 text-slate-400 font-medium">Timestamp</th>
                  <th className="text-left p-4 text-slate-400 font-medium">Admin</th>
                  <th className="text-left p-4 text-slate-400 font-medium">Action</th>
                  <th className="text-left p-4 text-slate-400 font-medium">Entity</th>
                  <th className="text-left p-4 text-slate-400 font-medium">IP</th>
                  <th className="text-right p-4 text-slate-400 font-medium">Details</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-slate-400">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-slate-400">
                      No logs found
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <React.Fragment key={log.log_id}>
                      <tr className="border-b border-slate-700/50 hover:bg-slate-700/30">
                        <td className="p-4 text-sm text-slate-400">
                          {formatDate(log.created_at)}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-slate-500" />
                            <span className="text-sm">{log.admin_email}</span>
                          </div>
                        </td>
                        <td className="p-4">
                          <Badge className={getActionColor(log.action)}>
                            {log.action.replace('_', ' ')}
                          </Badge>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-slate-500" />
                            <span className="text-sm">{log.entity_type}</span>
                            {log.entity_id && (
                              <span className="text-xs text-slate-500 font-mono">
                                {log.entity_id.slice(0, 8)}...
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-4 text-sm text-slate-400 font-mono">
                          {log.ip_address || 'N/A'}
                        </td>
                        <td className="p-4 text-right">
                          {log.metadata && Object.keys(log.metadata).length > 0 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setExpandedLog(expandedLog === log.log_id ? null : log.log_id)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                      {expandedLog === log.log_id && log.metadata && (
                        <tr className="bg-slate-900/50">
                          <td colSpan={6} className="p-4">
                            <pre className="text-xs text-slate-400 overflow-x-auto">
                              {JSON.stringify(log.metadata, null, 2)}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t border-slate-700">
              <p className="text-sm text-slate-400">Page {page} of {totalPages}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CMSAuditLogs;
